import { GuacParser, guacEncode } from "@cloudserver/contracts";
import { TUNNEL_STATE, type GuacStatus, type GuacTunnel } from "./guacamole.js";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Our Guacamole tunnel: the protocol in binary WebSocket frames on the
 * socket `connection.ts` opened and signed in (our JSON messages travel as
 * text frames on the same socket). Guacamole's own WebSocketTunnel would
 * put connection data in the URL and expects guacamole-client's servlet;
 * ours carries no secret and counts element lengths in code points, as
 * guacd does. One instruction per frame, as the API's relay expects.
 */
export class RdpTunnel implements GuacTunnel {
  state: number = TUNNEL_STATE.closed;
  uuid: string | null = null;
  receiveTimeout = 15_000;
  unstableThreshold = 1_500;
  oninstruction: ((opcode: string, args: string[]) => void) | null = null;
  onstatechange: ((state: number) => void) | null = null;
  onerror: ((status: GuacStatus) => void) | null = null;
  onuuid: ((uuid: string) => void) | null = null;
  private readonly parser = new GuacParser();

  constructor(private readonly socket: WebSocket) {}

  connect(): void {
    this.setState(TUNNEL_STATE.open);
  }

  disconnect(): void {
    this.setState(TUNNEL_STATE.closed);
  }

  sendMessage(...elements: unknown[]): void {
    if (elements.length === 0 || this.socket.readyState !== WebSocket.OPEN) return;
    const [opcode, ...args] = elements.map(String);
    this.socket.send(encoder.encode(guacEncode(opcode ?? "", ...args)));
  }

  isConnected(): boolean {
    return this.state === TUNNEL_STATE.open || this.state === TUNNEL_STATE.unstable;
  }

  setState(state: number): void {
    if (state === this.state) return;
    this.state = state;
    this.onstatechange?.(state);
  }

  setUUID(uuid: string): void {
    this.uuid = uuid;
    this.onuuid?.(uuid);
  }

  /** A binary frame from the API: whole instructions of guacd. */
  receive(frame: ArrayBuffer): void {
    for (const instruction of this.parser.receive(decoder.decode(frame))) {
      this.oninstruction?.(instruction.opcode, instruction.args);
    }
  }
}
